import React from 'react';

/**
 * ProfileAvatar - Avatar with thin gold border and colored gender symbol badge
 * @param {string} profileType - 'homme', 'femme', or 'couple'
 * @param {string} src - Image source URL
 * @param {string} fallback - Fallback text (usually first letter of name)
 * @param {string} size - Size class: 'sm', 'md', 'lg', 'xl'
 * @param {string} className - Additional CSS classes
 * @param {boolean} showOnline - Show online indicator
 * @param {boolean} isOnline - Is user online
 * @param {boolean} showGender - Show gender symbol
 */
export const ProfileAvatar = ({ 
  profileType = 'homme', 
  src, 
  fallback, 
  size = 'md',
  className = '',
  showOnline = false,
  isOnline = false,
  showGender = true
}) => {
  const sizes = {
    sm: { container: 'w-8 h-8', text: 'text-xs', badge: 'w-4 h-4 text-[8px]', badgePos: '-bottom-0.5 -right-0.5' },
    md: { container: 'w-10 h-10', text: 'text-sm', badge: 'w-5 h-5 text-[10px]', badgePos: '-bottom-0.5 -right-0.5' },
    lg: { container: 'w-12 h-12', text: 'text-base', badge: 'w-5 h-5 text-[10px]', badgePos: '-bottom-0.5 -right-0.5' },
    xl: { container: 'w-16 h-16', text: 'text-xl', badge: 'w-6 h-6 text-xs', badgePos: '-bottom-1 -right-1' },
    '2xl': { container: 'w-20 h-20', text: 'text-2xl', badge: 'w-7 h-7 text-sm', badgePos: '-bottom-1 -right-1' }
  };

  const currentSize = sizes[size] || sizes.md;

  // Render gender symbol with colors
  const renderGenderSymbol = () => {
    switch (profileType?.toLowerCase()) {
      case 'femme':
        return <span className="text-pink-400">♀</span>;
      case 'couple':
        return (
          <span className="flex items-center gap-px">
            <span className="text-pink-400">♀</span>
            <span className="text-blue-400">♂</span>
          </span>
        );
      case 'homme':
      default:
        return <span className="text-blue-400">♂</span>;
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Avatar with thin gold border */}
      <div 
        className={`${currentSize.container} rounded-full border-2 border-gold overflow-hidden`}
        style={{ boxShadow: '0 0 8px rgba(212, 175, 55, 0.3)' }}
      >
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-dark-subtle text-gold ${currentSize.text}`}>
            {fallback || '?'}
          </div>
        )}
      </div>
      
      {/* Gender symbol badge - positioned at bottom right */}
      {showGender && (
        <div 
          className={`absolute ${currentSize.badgePos} ${currentSize.badge} ${profileType === 'couple' ? 'w-7' : ''} rounded-full bg-dark border border-gold/50 flex items-center justify-center font-bold`}
          title={profileType === 'femme' ? 'Femme' : profileType === 'couple' ? 'Couple' : 'Homme'}
        >
          {renderGenderSymbol()}
        </div>
      )}
      
      {/* Online indicator - positioned at top right */}
      {showOnline && isOnline && (
        <span 
          className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full ring-2 ring-dark"
        />
      )}
    </div>
  );
};

export default ProfileAvatar;
